import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';
import api from '../services/api';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<{ prompts: any[]; tools: any[]; communities: any[] } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced live search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) { setSearchResults(null); setSearchOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(value.trim())}`);
        setSearchResults(res.data);
        setSearchOpen(true);
      } catch (error: any) {
        // Only log API errors in development to avoid exposing search queries
        if (process.env.NODE_ENV === 'development') {
          logger.logApiError('GET', `/search?q=${encodeURIComponent(value.trim())}`, error);
        }
        setSearchResults(null);
      }
    }, 300);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/prompts?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
      setIsMobileMenuOpen(false);
    }
  };

  const goTo = (path: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(path);
  };

  const hasResults = searchResults && (
    searchResults.prompts.length + searchResults.tools.length + searchResults.communities.length > 0
  );

  const handleLogout = () => {
    logout();
    toast.success(t('auth.logoutSuccess'));
    navigate('/');
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Skip link for keyboard / screen reader navigation */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
    <header
      style={{
        background: 'rgba(255, 255, 255, 0.88)',
        backdropFilter: 'blur(12px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
        borderBottom: '1px solid var(--border-default)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        transition: 'background 200ms ease, border-color 200ms ease',
      }}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo — Axioma */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" aria-label="Axioma - Home">
            <img
              src="/logo_principal.png"
              alt="Axioma"
              style={{ height: 52, objectFit: 'contain' }}
            />
          </Link>

          {/* Search — Desktop */}
          <div ref={searchRef} className="hidden md:flex flex-1 max-w-xs mx-10" style={{ position: 'relative' }}>
            <form onSubmit={handleSearch} style={{ width: '100%' }}>
              <div className="relative w-full">
                <MagnifyingGlassIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5"
                  style={{ color: 'var(--text-muted)' }}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => { if (searchResults && hasResults) setSearchOpen(true); }}
                  placeholder={t('header.searchPlaceholder')}
                  className="input pl-9 py-2 text-xs"
                  style={{ fontSize: 12 }}
                  autoComplete="off"
                />
              </div>
            </form>

            {/* Results dropdown */}
            {searchOpen && (
              <div
                className="animate-scale-in"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  right: 0,
                  background: 'var(--base)',
                  border: '1px solid var(--border-emphasis)',
                  borderRadius: 'var(--r-md)',
                  boxShadow: '0 8px 32px rgba(79,70,229,0.12), 0 2px 8px rgba(11,17,32,0.05)',
                  zIndex: 200,
                  overflow: 'hidden',
                  transformOrigin: 'top center',
                  maxHeight: 'min(480px, calc(100vh - 80px))',
                }}
              >
                {!hasResults ? (
                  <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.noResults')}
                  </div>
                ) : (
                  <div style={{ overflowY: 'auto', maxHeight: 'min(480px, calc(100vh - 80px))' }}>
                    {searchResults!.prompts.length > 0 && (
                      <div>
                        <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <SparklesIcon style={{ width: 10, height: 10, color: 'var(--signal)' }} />
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--text-secondary)', opacity: 0.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t('header.prompts')}</span>
                        </div>
                        {searchResults!.prompts.map((p) => (
                          <button key={p._id} onClick={() => goTo(`/prompts/${p._id}`)}
                            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                          >
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{p.title}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{p.category} · {p.aiTool}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {searchResults!.tools.length > 0 && (
                      <div style={{ borderTop: searchResults!.prompts.length > 0 ? '1px solid var(--border-default)' : undefined }}>
                        <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <WrenchScrewdriverIcon style={{ width: 10, height: 10, color: 'var(--signal)' }} />
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--text-secondary)', opacity: 0.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t('header.tools')}</span>
                        </div>
                        {searchResults!.tools.map((t) => (
                          <button key={t._id} onClick={() => goTo('/tools')}
                            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                          >
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{t.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t.category}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {searchResults!.communities.length > 0 && (
                      <div style={{ borderTop: (searchResults!.prompts.length + searchResults!.tools.length) > 0 ? '1px solid var(--border-default)' : undefined }}>
                        <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <UserGroupIcon style={{ width: 10, height: 10, color: 'var(--signal)' }} />
                          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--text-secondary)', opacity: 0.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{t('header.communities')}</span>
                        </div>
                        {searchResults!.communities.map((c) => (
                          <button key={c._id} onClick={() => goTo('/forums')}
                            style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                          >
                            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{c.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 240 }}>{c.description}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <div style={{ borderTop: '1px solid var(--border-default)', padding: '6px 14px 8px' }}>
                      <button onClick={() => goTo(`/prompts?search=${encodeURIComponent(searchQuery)}`)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <MagnifyingGlassIcon style={{ width: 11, height: 11 }} />
                        {t('header.search')} "{searchQuery}"
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Nav — Desktop */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/prompts"
              className={`nav-link ${isActive('/prompts') ? 'active' : ''}`}
            >
              {t('navigation.explore')}
            </Link>
            <Link
              to="/forums"
              className={`nav-link ${isActive('/forums') ? 'active' : ''}`}
            >
              {t('navigation.community')}
            </Link>
            <Link
              to="/tools"
              className={`nav-link ${isActive('/tools') ? 'active' : ''}`}
            >
              {t('navigation.tools')}
            </Link>
            <Link
              to="/blog"
              className={`nav-link ${isActive('/blog') ? 'active' : ''}`}
            >
              {t('header.blog')}
            </Link>

            <div
              style={{
                width: 1,
                height: 18,
                background: 'var(--border-default)',
                margin: '0 8px',
              }}
            />

            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/create"
                  className="btn btn-signal"
                  style={{ padding: '7px 14px', gap: 6, fontSize: 12 }}
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  {t('header.createPrompt')}
                </Link>

                <NotificationBell />

                {/* Avatar + dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="avatar-btn"
                  >
                    <img
                      src={
                        user?.avatar ||
                        `https://ui-avatars.com/api/?name=${user?.username}&background=F0F0F0&color=6B7280&bold=true&size=64`
                      }
                      alt={user?.username}
                      className="avatar"
                      style={{ width: 26, height: 26 }}
                    />
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0"
                        style={{ zIndex: 40 }}
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      <div
                        className="dropdown animate-scale-in absolute right-0 mt-1"
                        style={{ width: 200, zIndex: 50, transformOrigin: 'top right' }}
                      >
                        <div
                          style={{
                            padding: '12px 16px 10px',
                            borderBottom: '1px solid var(--border-default)',
                          }}
                        >
                          <p
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'var(--text-primary)',
                              fontFamily: 'Plus Jakarta Sans, sans-serif',
                            }}
                          >
                            {user?.username}
                          </p>
                          <p
                            style={{ fontSize: 11, color: 'var(--text-muted)' }}
                            className="truncate"
                          >
                            {user?.email}
                          </p>
                        </div>
                        <div style={{ padding: '4px 0' }}>
                          {[
                            { to: `/profile/${user?.username}`, label: t('common.profile') },
                            { to: '/my-prompts', label: t('header.myPrompts') },
                            { to: '/favorites', label: t('header.favorites') },
                          ].map((item) => (
                            <Link
                              key={item.to}
                              to={item.to}
                              className="dropdown-item"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              {item.label}
                            </Link>
                          ))}
                          {['admin', 'moderator'].includes(user?.role || '') && (
                            <Link
                              to="/admin"
                              className="dropdown-item"
                              style={{ color: 'var(--signal)' }}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              {t('navigation.admin')}
                            </Link>
                          )}
                          <Link
                            to="/settings"
                            className="dropdown-item"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            {t('common.settings')}
                          </Link>
                        </div>
                        <div
                          style={{ borderTop: '1px solid var(--border-default)', padding: '4px 0' }}
                        >
                          <button
                            onClick={handleLogout}
                            className="dropdown-item w-full text-left"
                            style={{ color: 'var(--destructive)' }}
                          >
                            {t('common.logout')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="btn btn-ghost" style={{ padding: '7px 14px', fontSize: 12 }}>
                  {t('auth.login')}
                </Link>
                <Link to="/register" className="btn btn-signal" style={{ padding: '7px 14px', fontSize: 12 }}>
                  {t('auth.register')}
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            style={{ color: 'var(--text-secondary)', padding: 8, borderRadius: 'var(--r-sm)', border: 'none', background: 'transparent', cursor: 'pointer', minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Bars3Icon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <nav
          id="mobile-menu"
          className="md:hidden animate-slide-down"
          style={{ borderTop: '1px solid var(--border-default)', padding: '16px 20px 24px' }}
          aria-label="Mobile navigation"
        >
          <form onSubmit={handleSearch} className="mb-4" role="search" aria-label="Search">
            <div className="relative">
              <MagnifyingGlassIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                style={{ color: 'var(--text-muted)' }}
                aria-hidden="true"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('header.search')}
                className="input pl-10"
                style={{ fontSize: 14, minHeight: 44 }}
                autoComplete="off"
              />
            </div>
          </form>

          <div className="flex flex-col gap-1">
            {[
              { to: '/prompts', label: t('navigation.explore') },
              { to: '/forums',  label: t('navigation.community') },
              { to: '/tools',   label: t('navigation.tools') },
              { to: '/blog',    label: t('header.blog') },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`nav-link ${isActive(item.to) ? 'active' : ''}`}
                style={{ minHeight: 44, fontSize: 14 }}
              >
                {item.label}
              </Link>
            ))}

            <div style={{ height: 1, background: 'var(--border-default)', margin: '8px 0' }} role="separator" />

            {isAuthenticated ? (
              <>
                <Link
                  to="/create"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn btn-signal"
                  style={{ justifyContent: 'center', marginBottom: 4, minHeight: 44 }}
                >
                  + {t('header.createPrompt')}
                </Link>
                {[
                  { to: `/profile/${user?.username}`, label: t('common.profile') },
                  { to: '/my-prompts', label: t('header.myPrompts') },
                  { to: '/favorites', label: t('header.favorites') },
                  { to: '/settings', label: t('common.settings') },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="nav-link"
                    style={{ minHeight: 44, fontSize: 14 }}
                  >
                    {item.label}
                  </Link>
                ))}
                <button
                  onClick={handleLogout}
                  className="nav-link text-left"
                  style={{ color: 'var(--destructive)', minHeight: 44, fontSize: 14 }}
                >
                  {t('common.logout')}
                </button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn btn-ghost"
                  style={{ justifyContent: 'center', minHeight: 44 }}
                >
                  {t('auth.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="btn btn-signal"
                  style={{ justifyContent: 'center', minHeight: 44 }}
                >
                  {t('auth.register')}
                </Link>
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
    </>
  );
};

export default Header;
